import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { cn } from '../../lib/cn';

/**
 * Popover — shadcn idiom on `@radix-ui/react-popover`, themed onto the HofSync
 * brand tokens. Like Select/Tooltip/Dialog, this is a Radix **portal**
 * primitive: the bubble (`PopoverContent`) renders through
 * `PopoverPrimitive.Portal` to `document.body`, OUTSIDE the DS subtree. The
 * global compiled stylesheet `dist/styles.css` still styles it, but ONLY
 * because every utility on the portal content is `tw:`-prefixed (see CLAUDE.md
 * "Scoping"). There is no native UA chrome to reset on the content — Radix
 * renders a plain `<div>` — but because preflight is excluded we still set the
 * surface/border/shadow explicitly on the brand `--popover` / `--border`
 * tokens. (The trigger is typically composed via `asChild` onto a DS `Button`,
 * which carries its own UA reset.)
 *
 * A11y contract: `PopoverContent` renders `role="dialog"` — consumers MUST
 * give it an accessible name (`aria-label` or `aria-labelledby`) or axe fails
 * the aria-dialog-name check.
 *
 * `@radix-ui/react-popover` is a real `dependency` (the DS ships as source);
 * the names are collision-free in the public-API barrel.
 */
const Popover = PopoverPrimitive.Root;
const PopoverTrigger = PopoverPrimitive.Trigger;
const PopoverAnchor = PopoverPrimitive.Anchor;

export type PopoverProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Root
>;
export type PopoverContentProps = React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
>;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(({ className, align = 'center', sideOffset = 4, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      // Portal content: every utility MUST be tw:-prefixed (it mounts on
      // document.body, styled only by the global dist/styles.css).
      className={cn(
        'tw:z-50 tw:w-72 tw:rounded-md tw:border tw:border-border tw:bg-popover tw:p-4 tw:text-sm tw:text-popover-foreground tw:shadow-md tw:outline-none',
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
